// Enum mirroring backend InvestmentType (keep numeric values aligned)
export enum InvestmentType {
  Stock = 1,
  ETF = 2,
  Bond = 3,
  MutualFund = 4,
  Cryptocurrency = 5,
  RealEstate = 6,
  Commodity = 7,
  CFD = 8
}

// Mirror backend CurrencyCode enum
export enum CurrencyCode {
  EUR = 'EUR',
  USD = 'USD',
  GBP = 'GBP',
  CHF = 'CHF',
  JPY = 'JPY'
}

export interface StockDto {
  publicId: string;
  symbol: string;
  investmentType: InvestmentType; // Added to align with backend StockDto
  currentPrice?: number;
  currentCambio?: number;
  currency: CurrencyCode;
  purchases: PurchaseDto[];
  dividends: DividendDto[];
  sales: SaleDto[];
}

export interface PurchaseDto {
  publicId: string;
  date: string;
  quantity: number;
  pricePerShare: number;
  cambio?: number;
  pricePerShareEUR?: number;
  totalAmountEUR?: number;
}

// Command used for creating a purchase (no publicId supplied by client)
export interface CreatePurchaseCommand {
  date: string;
  quantity: number;
  pricePerShare: number;
  cambio?: number;
}

export interface DividendDto {
  publicId: string;
  date: string;
  grossAmount: number;
  taxPaid: number;
  amount: number;
}

export interface SaleDto {
  publicId: string;
  date: string;
  quantity: number;
  pricePerShare: number;
  totalAmount: number;
  gainLoss: number;
  cambio?: number;
  pricePerShareEUR?: number;
  totalAmountEUR?: number;
  salePurchases: SalePurchaseDto[];
}

interface SalePurchaseDto {
  purchasePublicId: string;
  quantitySold: number;
  gainLoss: number;
}

export interface UpdateSaleCommand {
  date: string;
  quantity: number;
  pricePerShare: number;
  cambio?: number;
}

export interface CreateSaleCommand {
  date: string;
  quantity: number;
  pricePerShare: number;
  cambio?: number;
}

// Command objects for stock creation/updating (avoid using Partial<StockDto>)
export interface CreateStockCommand {
  symbol: string;
  investmentType: InvestmentType;
  currentPrice?: number;
  // Optional current cambio (quote currency → EUR) for the stock
  currentCambio?: number;
  currency: CurrencyCode;
}

export interface UpdateStockCommand {
  symbol?: string; // allow symbol change if backend permits
  investmentType?: InvestmentType; // optional for partial updates
  currentPrice?: number;
  // Optional current cambio (quote currency → EUR) for the stock
  currentCambio?: number;
  currency?: CurrencyCode;
}

export interface StockAnalysisDto {
  symbol: string;
  stock: StockDto;
  totalInvested: number;
  currentValue: number;
  profitLoss: number;
  profitLossPercentage: number;
  totalDividends: number;
  totalReturn: number;
  totalReturnPercentage: number;
  purchases: PurchaseDto[];
  dividends: DividendDto[];
  sales: SaleDto[];
}

export interface PortfolioSummaryDto {
  totalInvested: number;
  totalValue: number;
  totalReturn: number;
  avgReturn: number;
  grossDividends: number;
  netDividends: number;
  taxPaid: number;
  avgDividendYield: number;
}

export interface StockImportResultDto {
  success: boolean;
  message: string;
  totalStocks: number;
  totalPurchases: number;
  totalDividends: number;
  totalSales: number;
  failed: number;
  errors: string[];
  warnings: string[];
}
